import React, { useCallback, useEffect, useState } from 'react';
import {
    CapType,
    createCapType,
    deleteCapType,
    updateCapType,
    listCapTypes,
} from '../../api/captype';
import { alertApprovalIfPending, ApiError } from '../../api/request';
import {
    CapDevicesModal,
    invalidateCapTypesCache,
} from '../../components/CapTypeBadges';
import DictSelect, { useDictText } from '../../components/DictSelect';
import Modal from '../../components/Modal';
import CapDeviceBindModal from './CapDeviceBindModal';
import BatchActions, {
    HeaderCheckbox,
    RowCheckbox,
} from '../../components/BatchActions';
import { useBatchSelect } from '../../utils/useBatchSelect';
import { confirmDialog, alertDialog } from '../../utils/dialog';
import { useCan } from '../../store';

// capKey 建议值: 与存量 key 同风格的驼峰 (light + Action), 如 blue+eng → blueEng, blade-cutting+cut → bladeCuttingCut.
// 只作默认值, 用户可改; 唯一性由后端 cap_key 业务主键判重 (冲突返回错误)。
const camel = (v: string) =>
    v
        .toLowerCase()
        .split(/[^a-z0-9]+/i)
        .filter(Boolean)
        .map((w, i) => (i === 0 ? w : w.charAt(0).toUpperCase() + w.slice(1)))
        .join('');
const suggestCapKey = (light: string, action: string) => {
    const l = camel(light);
    const a = camel(action);
    if (!l) return a;
    return a ? l + a.charAt(0).toUpperCase() + a.slice(1) : l;
};

// 工艺类型管理: 维护「适配工艺类型」字段 + 每个工艺类型"具备设备"的显式绑定 (2026-09-08).
// 光源/动作是可选描述 (非激光工艺类型留空), 不再用来推断设备.
// admin 新增/删除即时反映到 材料库/材料详情/SKU库 的工艺类型徽标;
// 普通用户提交后走审批工单, 通过后才生效.
function CraftTypeManage() {
    const canCreate = useCan('cap_type', 'create');
    const canUpdate = useCan('cap_type', 'update');
    const canDelete = useCan('cap_type', 'delete');
    const canManage = canUpdate || canDelete;
    const [list, setList] = useState<CapType[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // 查看具备设备弹窗 / 绑定设备弹窗
    const [openCap, setOpenCap] = useState<CapType | null>(null);
    const [bindCap, setBindCap] = useState<CapType | null>(null);
    // 列表展示光源/动作的中文名 (字典 light_source / cap_action), 不直接渲染 code
    const lightText = useDictText('light_source');
    const actionText = useDictText('cap_action');
    // 新增/编辑弹窗 (editingCap 非空 = 编辑态, capKey 锁定)
    const [showAdd, setShowAdd] = useState(false);
    const [editingCap, setEditingCap] = useState<CapType | null>(null);
    const [addName, setAddName] = useState('');
    const [addLight, setAddLight] = useState('blue');
    const [addAction, setAddAction] = useState('eng');
    // 新增时的字段 key: 默认跟随光源/动作生成, 用户手改后 (keyTouched) 不再覆盖
    const [addKey, setAddKey] = useState('');
    const [keyTouched, setKeyTouched] = useState(false);
    const [busy, setBusy] = useState(false);

    // 批量操作 (2026-09-10): 工艺类型不分页, 列表刷新即重置选择
    const sel = useBatchSelect(
        list.map((c) => c.capKey),
        String(list.length),
    );

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await listCapTypes();
            // 具备设备数直接取 deviceKeys 长度 (含停用设备), 不再逐个 cap 反查
            setList([...(res || [])].sort((a, b) => a.sort - b.sort));
        } catch (e) {
            const msg =
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
            setError(msg);
            setList([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const openEdit = (c: CapType) => {
        setEditingCap(c);
        setAddName(c.label);
        setAddLight(c.lightSource || '');
        setAddAction(c.action || '');
        setAddKey(c.capKey);
        setKeyTouched(true); // 编辑态 key 用现值, 不跟随光源/动作重算
        setShowAdd(true);
    };

    const closeDialog = () => {
        setShowAdd(false);
        setEditingCap(null);
        setAddName('');
        setAddLight('blue');
        setAddAction('eng');
        setAddKey('');
        setKeyTouched(false);
    };

    const onAdd = async () => {
        const name = addName.trim();
        if (!name) {
            alertDialog('请填写工艺类型名称');
            return;
        }
        const capKey = (addKey || suggestCapKey(addLight, addAction)).trim();
        if (!capKey) {
            alertDialog('请填写字段 key');
            return;
        }
        if (
            editingCap &&
            capKey !== editingCap.capKey &&
            !(await confirmDialog(
                `确认把字段 key 从「${editingCap.capKey}」改为「${capKey}」?\n已打在材料上的能力位会一并改名 (材料 updatedAt 会更新, 下游按水位同步)。`,
            ))
        ) {
            return;
        }
        // 光源/动作可留空 (非激光工艺类型); 后端只校验非空值在字典内
        setBusy(true);
        try {
            const res = editingCap
                ? await updateCapType(editingCap.capKey, {
                      newCapKey: capKey,
                      label: name,
                      lightSource: addLight,
                      action: addAction,
                  })
                : await createCapType({
                      // capKey 由用户填写 (默认按 光源+动作 驼峰生成), 唯一性由后端判重
                      capKey,
                      label: name,
                      lightSource: addLight,
                      action: addAction,
                  });
            invalidateCapTypesCache();
            closeDialog();
            // 走审批流时数据未变, load 只是回读原列表
            await load();
            alertApprovalIfPending(res.message);
        } catch (e) {
            const msg =
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
            alertDialog(`${editingCap ? '编辑' : '新增'}失败: ` + msg);
        } finally {
            setBusy(false);
        }
    };

    const onDelete = async (c: CapType) => {
        if (!(await confirmDialog(`确认删除工艺类型「${c.label}」?`))) return;
        try {
            const res = await deleteCapType(c.capKey);
            invalidateCapTypesCache();
            await load();
            alertApprovalIfPending(res.message);
        } catch (e) {
            const msg =
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
            alertDialog('删除失败: ' + msg);
        }
    };

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-base font-semibold">工艺类型管理</div>
                    <div className="text-xs text-slate-500">
                        维护「适配工艺类型」字段及其具备设备;
                        新增后即可在材料/SKU 的适配工艺类型中使用 · 共{' '}
                        {list.length} 项
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <BatchActions
                        resource="cap_type"
                        entityLabel="工艺类型"
                        deleteKeyField="capKey"
                        selected={sel.selectedKeys}
                        onClear={sel.clear}
                        onDone={load}
                    />
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={() => setShowAdd(true)}
                        hidden={!canCreate}
                    >
                        + 新增工艺类型
                    </button>
                </div>
            </div>

            {error && (
                <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                    错误: {error}
                </div>
            )}

            <div className="card overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-slate-50">
                        <tr>
                            <th className="th w-8" hidden={!canDelete}>
                                <HeaderCheckbox hidden={!canDelete} sel={sel} />
                            </th>
                            <th className="th">工艺类型</th>
                            <th className="th">字段 key</th>
                            <th className="th">光源</th>
                            <th className="th">动作</th>
                            <th className="th">具备设备</th>
                            <th className="th" hidden={!canManage}>
                                操作
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td
                                    className="td text-center text-slate-400"
                                    colSpan={7}
                                >
                                    加载中…
                                </td>
                            </tr>
                        )}
                        {!loading && list.length === 0 && (
                            <tr>
                                <td
                                    className="td text-center text-slate-400"
                                    colSpan={7}
                                >
                                    暂无工艺类型, 点击右上角「+ 新增工艺类型」
                                </td>
                            </tr>
                        )}
                        {!loading &&
                            list.map((c) => {
                                const n = (c.deviceKeys || []).length;
                                return (
                                    <tr
                                        key={c.capKey}
                                        className="hover:bg-slate-50"
                                    >
                                        <td className="td" hidden={!canDelete}>
                                            <RowCheckbox
                                                hidden={!canDelete}
                                                sel={sel}
                                                k={c.capKey}
                                            />
                                        </td>
                                        <td className="td">
                                            <span className="tag cap-on">
                                                {c.label}
                                            </span>
                                        </td>
                                        <td className="td font-mono text-xs text-slate-500">
                                            {c.capKey}
                                        </td>
                                        {/* 展示中文名, code 挂 title 备查 */}
                                        <td
                                            className="td text-xs"
                                            title={c.lightSource}
                                        >
                                            {lightText(c.lightSource)}
                                        </td>
                                        <td
                                            className="td text-xs"
                                            title={c.action}
                                        >
                                            {actionText(c.action)}
                                        </td>
                                        <td className="td text-xs whitespace-nowrap">
                                            {n > 0 ? (
                                                <button
                                                    type="button"
                                                    className="text-brand-600 hover:underline"
                                                    title="查看绑定的设备 (仅启用中的)"
                                                    onClick={() =>
                                                        setOpenCap(c)
                                                    }
                                                >
                                                    {n} 台 →
                                                </button>
                                            ) : (
                                                <span className="text-slate-300">
                                                    未绑定
                                                </span>
                                            )}
                                            <button
                                                type="button"
                                                className="ml-2 text-slate-500 hover:text-brand-600 hover:underline"
                                                title="绑定/解绑具备该工艺类型的设备"
                                                onClick={() => setBindCap(c)}
                                                hidden={!canUpdate}
                                            >
                                                绑定
                                            </button>
                                        </td>
                                        <td
                                            className="td whitespace-nowrap"
                                            hidden={!canManage}
                                        >
                                            <button
                                                type="button"
                                                className="text-brand-600 hover:underline text-xs mr-2"
                                                onClick={() => openEdit(c)}
                                                hidden={!canUpdate}
                                            >
                                                编辑
                                            </button>
                                            <button
                                                type="button"
                                                className="btn-danger-text"
                                                onClick={() => onDelete(c)}
                                                hidden={!canDelete}
                                            >
                                                删除
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                    </tbody>
                </table>
            </div>

            <div className="text-[11px] text-slate-400">
                材料/SKU 的适配工艺类型徽标由此列表驱动,
                新增/删除会即时反映到各处; 「具备设备」是显式绑定
                (点「绑定」维护, 不限框架, 非激光设备也可绑),
                徽标弹层与导出标签都以绑定为准。
            </div>

            <CapDevicesModal cap={openCap} onClose={() => setOpenCap(null)} />
            <CapDeviceBindModal
                cap={bindCap}
                onClose={() => setBindCap(null)}
                onSaved={() => {
                    invalidateCapTypesCache();
                    load();
                }}
            />

            <Modal
                open={showAdd}
                title={
                    editingCap
                        ? `编辑工艺类型 · ${editingCap.capKey}`
                        : '新增工艺类型'
                }
                width={520}
                onClose={closeDialog}
            >
                <div className="space-y-4">
                    <div>
                        <label className="text-xs text-slate-500 block mb-1">
                            工艺类型名称
                        </label>
                        <input
                            className="inp w-full"
                            placeholder="例: 蓝光雕 / UV切 / 刀切 / 热转印"
                            value={addName}
                            onChange={(e) => setAddName(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="text-xs text-slate-500 block mb-1">
                            字段 key
                            <span className="ml-1 text-slate-400">
                                (标识, 建议驼峰英文; 默认按光源+动作生成, 可改)
                            </span>
                        </label>
                        <div className="flex gap-2">
                            <input
                                className="inp w-full font-mono"
                                placeholder={
                                    suggestCapKey(addLight, addAction) ||
                                    '例: bladeCut'
                                }
                                value={addKey}
                                onChange={(e) => {
                                    setKeyTouched(true);
                                    setAddKey(e.target.value);
                                }}
                            />
                            <button
                                type="button"
                                className="btn-ghost whitespace-nowrap"
                                title="按当前光源+动作重新生成"
                                onClick={() => {
                                    setAddKey(
                                        suggestCapKey(addLight, addAction),
                                    );
                                    setKeyTouched(false);
                                }}
                                hidden={!canUpdate}
                            >
                                重新生成
                            </button>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-slate-500 block mb-1">
                                光源 (可选)
                            </label>
                            {/* 选项来自「字典维护 · 光源 (light_source)」, 与后端校验同源; 非激光留空 */}
                            <DictSelect
                                category="light_source"
                                className="sel w-full"
                                value={addLight}
                                onChange={(v) => {
                                    setAddLight(v);
                                    if (!keyTouched)
                                        setAddKey(suggestCapKey(v, addAction));
                                }}
                                emptyLabel="(无 · 非激光)"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-slate-500 block mb-1">
                                动作 (可选)
                            </label>
                            {/* 选项来自「字典维护 · 动作 (cap_action)」, 与后端校验同源 */}
                            <DictSelect
                                category="cap_action"
                                className="sel w-full"
                                value={addAction}
                                onChange={(v) => {
                                    setAddAction(v);
                                    if (!keyTouched)
                                        setAddKey(suggestCapKey(addLight, v));
                                }}
                                emptyLabel="(无)"
                            />
                        </div>
                    </div>
                    <div className="text-[11px] text-slate-400">
                        {editingCap
                            ? '字段 key 可改: 保存时会级联更新已打在材料上的能力位 (key 只是标识, 含义由名称/光源/动作承载)。具备设备在列表「绑定」里维护。'
                            : '保存后作为新的适配工艺类型字段, 出现在材料/SKU 的适配工艺类型与本管理列表中; 字段 key 默认按 光源+动作 驼峰生成 (如 blueEng), 可自行改写, 重复会被拒。保存后再在列表点「绑定」挂设备。'}
                    </div>
                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            className="btn-ghost"
                            disabled={busy}
                            onClick={closeDialog}
                        >
                            取消
                        </button>
                        <button
                            type="button"
                            className="btn-primary"
                            disabled={busy}
                            onClick={onAdd}
                        >
                            {busy ? '保存中…' : '保存'}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

export default CraftTypeManage;
